import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Stack,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Breadcrumbs,
  Link,
  InputAdornment,
  TablePagination,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { get, post } from 'src/utils/networkUtils';

export default function OtherLeagueImportPage() {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [selectedTcId, setSelectedTcId] = useState('');
  const [targetTournaments, setTargetTournaments] = useState([]);
  const [selectedTargetTcId, setSelectedTargetTcId] = useState('');
  const [matches, setMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [importingIds, setImportingIds] = useState(new Set());
  const [importResults, setImportResults] = useState({});

  // Filters
  const [teamSearch, setTeamSearch] = useState('');
  const [importStatusFilter, setImportStatusFilter] = useState('all');
  const [matchStatusFilter, setMatchStatusFilter] = useState('all');
  const [forceFixtureStatus, setForceFixtureStatus] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    get('/admin/opta/tournaments?filter=other')
      .then(r => {
        const items = r?.data ?? [];
        setTournaments(items);
        if (items.length > 0) setSelectedTcId(items[0].id);
      })
      .catch(() => setTournaments([]));

    get('/admin/opta/tournaments?filter=allowed')
      .then(r => {
        const items = r?.data ?? [];
        setTargetTournaments(items);
        if (items.length > 0) setSelectedTargetTcId(items[0].id);
      })
      .catch(() => setTargetTournaments([]));
  }, []);

  const resolvedUuid = () => {
    const tc = tournaments.find(t => t.id === selectedTcId);
    return tc?.opta_tournament_calendar_id ?? '';
  };

  const loadMatches = async () => {
    const uuid = resolvedUuid();
    if (!uuid) {
      setLoadError('Pick a tournament first.');
      return;
    }
    setLoadError(null);
    setLoadingMatches(true);
    setMatches([]);
    setImportResults({});
    try {
      const r = await get(
        `/admin/opta/matches/preview?tournamentCalendarUuid=${encodeURIComponent(uuid)}`
      );
      setMatches(r?.data ?? []);
    } catch (err) {
      setLoadError(err?.response?.data?.message ?? err?.message ?? 'Failed to load matches');
    } finally {
      setLoadingMatches(false);
    }
  };

  const importMatch = async fixtureId => {
    const uuid = resolvedUuid();
    if (!uuid || !selectedTargetTcId) return;

    setImportingIds(prev => new Set(prev).add(fixtureId));
    setImportResults(prev => ({ ...prev, [fixtureId]: null }));

    try {
      const r = await post('/admin/opta/matches/import-single', {
        fixtureId,
        tournamentCalendarUuid: uuid,
        targetTournamentCalendarId: selectedTargetTcId,
        forceFixtureStatus,
      });
      setImportResults(prev => ({
        ...prev,
        [fixtureId]: { success: true, ...r?.data },
      }));
    } catch (err) {
      setImportResults(prev => ({
        ...prev,
        [fixtureId]: {
          success: false,
          message: err?.response?.data?.message ?? err?.message ?? 'Import failed',
        },
      }));
    } finally {
      setImportingIds(prev => {
        const next = new Set(prev);
        next.delete(fixtureId);
        return next;
      });
    }
  };

  const formatDateTime = (date, time) => {
    if (!date) return '-';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return date;
    return `${d.toLocaleDateString()} ${time ?? ''}`;
  };

  // Apply filters
  const filteredMatches = matches.filter(m => {
    const searchLower = teamSearch.toLowerCase();
    const teamMatch =
      !searchLower ||
      (m.homeTeam?.name ?? '').toLowerCase().includes(searchLower) ||
      (m.awayTeam?.name ?? '').toLowerCase().includes(searchLower);

    let importMatch = true;
    if (importStatusFilter === 'imported') {
      importMatch = m.alreadyImported === true;
    } else if (importStatusFilter === 'not-imported') {
      importMatch = m.alreadyImported === false;
    }

    const statusMatch = matchStatusFilter === 'all' || m.status === matchStatusFilter;

    return teamMatch && importMatch && statusMatch;
  });

  // Paginate
  const paginatedMatches = filteredMatches.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <Helmet>
        <title>Additional Leagues Import | EngageCraft Fantasy Football Admin</title>
      </Helmet>
      <Container maxWidth="xl">
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate('/dashboard/ecff/opta-import')}
            underline="hover"
            sx={{ cursor: 'pointer' }}
          >
            OPTA Import
          </Link>
          <Typography variant="body1" color="text.primary">
            Additional Leagues Import
          </Typography>
        </Breadcrumbs>
        <Typography variant="h4" sx={{ mb: 1 }}>
          Additional Leagues Import
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Import individual fixtures from additional leagues. Each match will be linked to the
          selected target tournament. Only fixtures and contestants are imported — no players are
          added. Match events (goals, cards, subs) are imported if the players already exist in the
          database.
        </Typography>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
              <TextField
                select
                label="Source tournament"
                value={selectedTcId}
                onChange={e => setSelectedTcId(e.target.value)}
                sx={{ minWidth: 320 }}
              >
                {tournaments.length === 0 && (
                  <MenuItem value="" disabled>
                    No tournaments yet
                  </MenuItem>
                )}
                {tournaments.map(t => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.competition_name ?? '(unnamed)'} {t.season_label ?? ''} -{' '}
                    {t.opta_tournament_calendar_id?.slice(0, 8)}…
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Target tournament"
                value={selectedTargetTcId}
                onChange={e => setSelectedTargetTcId(e.target.value)}
                sx={{ minWidth: 320 }}
              >
                {targetTournaments.length === 0 && (
                  <MenuItem value="" disabled>
                    No target tournaments yet
                  </MenuItem>
                )}
                {targetTournaments.map(t => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.competition_name ?? '(unnamed)'} {t.season_label ?? ''} -{' '}
                    {t.opta_tournament_calendar_id?.slice(0, 8)}…
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="contained"
                onClick={loadMatches}
                disabled={loadingMatches || !resolvedUuid()}
                startIcon={loadingMatches ? <CircularProgress size={16} /> : null}
              >
                {loadingMatches ? 'Loading…' : 'Load Matches'}
              </Button>
              <FormControlLabel
                control={
                  <Switch
                    checked={forceFixtureStatus}
                    onChange={e => setForceFixtureStatus(e.target.checked)}
                  />
                }
                label="Force fixture status (simulator testing)"
                sx={{ ml: 1 }}
              />
            </Stack>
            {loadError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {loadError}
              </Alert>
            )}
          </CardContent>
        </Card>

        {matches.length > 0 && (
          <>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
                  <TextField
                    label="Search Teams"
                    placeholder="Filter by team name..."
                    value={teamSearch}
                    onChange={e => {
                      setTeamSearch(e.target.value);
                      setPage(0);
                    }}
                    sx={{ minWidth: 240 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">🔍</InputAdornment>,
                    }}
                  />
                  <TextField
                    select
                    label="Import Status"
                    value={importStatusFilter}
                    onChange={e => {
                      setImportStatusFilter(e.target.value);
                      setPage(0);
                    }}
                    sx={{ minWidth: 180 }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="not-imported">Not Imported</MenuItem>
                    <MenuItem value="imported">Imported</MenuItem>
                  </TextField>
                  <TextField
                    select
                    label="Match Status"
                    value={matchStatusFilter}
                    onChange={e => {
                      setMatchStatusFilter(e.target.value);
                      setPage(0);
                    }}
                    sx={{ minWidth: 180 }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="fixture">Fixture</MenuItem>
                    <MenuItem value="playing">Playing</MenuItem>
                    <MenuItem value="played">Played</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                    <MenuItem value="postponed">Postponed</MenuItem>
                  </TextField>
                </Stack>
              </CardContent>
            </Card>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Matches ({filteredMatches.length} of {matches.length})
            </Typography>
            <Card>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Home</TableCell>
                      <TableCell>Away</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>GW</TableCell>
                      <TableCell>Venue</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedMatches.map(m => {
                      const isImporting = importingIds.has(m.fixtureId);
                      const result = importResults[m.fixtureId];
                      return (
                        <TableRow key={m.fixtureId} hover>
                          <TableCell>{formatDateTime(m.date, m.time)}</TableCell>
                          <TableCell>{m.homeTeam?.name ?? '-'}</TableCell>
                          <TableCell>{m.awayTeam?.name ?? '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={m.status}
                              size="small"
                              color={
                                m.status === 'played'
                                  ? 'success'
                                  : m.status === 'playing'
                                    ? 'warning'
                                    : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell>{m.gameweek ?? '-'}</TableCell>
                          <TableCell>{m.venue ?? '-'}</TableCell>
                          <TableCell align="right">
                            {m.alreadyImported ? (
                              <Tooltip title="Already imported">
                                <Chip
                                  label="Imported"
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                              </Tooltip>
                            ) : isImporting ? (
                              <CircularProgress size={20} />
                            ) : result?.success ? (
                              <Tooltip
                                title={`Events: ${result.eventsInserted} inserted, ${result.eventsSkipped} skipped | Stats: ${result.statsInserted ?? 0} inserted`}
                              >
                                <Chip label="Done" size="small" color="success" />
                              </Tooltip>
                            ) : result ? (
                              <Tooltip title={result.message}>
                                <Chip label="Failed" size="small" color="error" />
                              </Tooltip>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => importMatch(m.fixtureId)}
                                disabled={!selectedTargetTcId}
                              >
                                Import
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={filteredMatches.length}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={e => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
                {filteredMatches.length === 0 && matches.length > 0 && (
                  <Alert severity="info" sx={{ m: 2 }}>
                    No matches match the current filters.
                  </Alert>
                )}
              </TableContainer>
            </Card>
          </>
        )}
      </Container>
    </>
  );
}
